import {
  autoFormatDocument,
  formatCurrency,
  formatCurrencyColor,
  formatCurrencyType,
  formatDocument,
  formatPhone,
} from "../../Utils/formatters";
import moment from "moment";
import { getTransactionsByDay } from "../../Utils/getTransactionsByDay";
import { normalizeIbProductFeatures } from "../../Utils/ibProductFeatures";
import { buildReceiptFromStatementItem, isStatementRefund } from "../../Utils/buildReceiptFromStatementItem";
import {
  getStatementEndToEndId,
  getStatementOperationId,
} from "../../Utils/mapStatementTransactionRow";

export const normalizeAuthStep2Data = ({ data }) => ({
  created: data?.created,
  tokenApp: data?.token_id,
  expiresIn: data?.expires_in,
});

export const normalizeUserData = ({ data }) => ({
  userLanguage: data?.language || "pt-BR",
  userId: data?.id || 0,
});

export const normalizeAccountData = ({ data }) => {
  const documentRaw = data?.document || "";
  const documentDigits = documentRaw.replace(/\D/g, "");
  const documentType =
    data?.document_type ||
    (documentDigits.length > 11 ? "CNPJ" : documentDigits.length > 0 ? "CPF" : "");
  const documentLabel =
    formatDocument(documentRaw, documentType) || autoFormatDocument(documentDigits) || "";
  const accountCode = data?.account?.acount_code || data?.account?.account_code || "";

  let userSubTitle = "";
  if (documentLabel && accountCode) {
    userSubTitle = `${documentLabel} - Conta: ${accountCode}`;
  } else if (documentLabel) {
    userSubTitle = documentLabel;
  } else if (accountCode) {
    userSubTitle = `Conta: ${accountCode}`;
  }

  const features = normalizeIbProductFeatures(data?.features);

  return {
    userName: data?.full_name || "",
    userSubTitle,
    userURL: data?.receipt?.qrcode_link || "",
    userId: data?.id || "",
    userEmail: data?.email || "",
    userPhone: formatPhone(data?.phone?.phone_number, "CEL") || "",
    userAddress: `${data?.address?.address}, ${data?.address?.number} - ${data?.address?.district} - ${data?.address?.city} - ${data?.address?.state}`,
    userDocument: formatDocument(documentRaw, documentType) || autoFormatDocument(documentDigits) || "",
    userDocumentRaw: documentRaw,
    userDocumentType: documentType,
    transactionalPasswordQualified: data?.transactional_password_qualified === true,
    documentPending: data?.document_pending === true,
    pending: data?.pending === true,
    // Person KYC status for Conta Individualizada banner — show when !== verified.
    personStatus:
      data?.person_status
      || data?.personStatus
      || (typeof data?.status === "string" && data.status
        ? data.status
        : undefined)
      || (data?.document_pending === true || data?.pending === true
        ? "pending"
        : data?.document_pending === false
          ? "verified"
          : undefined),
    verificationStatus: data?.verification_status || data?.verificationStatus || null,
    waitingAccount: data?.waiting_account === true || data?.waitingAccount === true,
    accountLifecycle: data?.account_lifecycle || data?.accountLifecycle || null,
    activeAccountId: data?.active_account_id || data?.activeAccountId || null,
    accounts: Array.isArray(data?.accounts) ? data.accounts : [],
    // Caller must sync to the store in useEffect — never during render.
    features: data?.features ? features : undefined,
  };
};

/**
 * Account-wide Over Balance (limit) in cents.
 * BaaS sends `limit_cents` (preferred) and `limit` as major BRL — never /100 on `limit`.
 * Take the max of both so a zero `limit_cents` does not hide a non-zero major `limit`.
 */
const pickLimitCents = (row) => {
  if (row == null) return 0;
  let fromCents = 0;
  const limitCentsRaw = row.limit_cents ?? row.limitCents ?? row.LimitCents;
  if (limitCentsRaw != null && limitCentsRaw !== "") {
    const asCents = Number(limitCentsRaw);
    if (Number.isFinite(asCents) && asCents > 0) fromCents = asCents;
  }
  let fromMajor = 0;
  // Prefer explicit major fields. Only fall back to `limit`/`Limit` when cents
  // were missing — IbPersonBalanceRow.Limit is cents and must not be *100 again.
  const limitMajor =
    row.limit_major
    ?? row.limitMajor
    ?? row.LimitMajor
    ?? (fromCents > 0 ? null : (row.limit ?? row.Limit));
  if (limitMajor != null && limitMajor !== "") {
    const asMajor = Number(limitMajor);
    if (Number.isFinite(asMajor) && asMajor > 0) {
      fromMajor = Math.round(asMajor * 100);
    }
  }
  return Math.max(fromCents, fromMajor);
};

const pickAllowOverdraft = (row) => {
  if (row == null) return false;
  const raw = row.allow_overdraft ?? row.allowOverdraft ?? row.AllowOverdraft;
  return raw === true || raw === 1 || raw === "1" || raw === "true";
};

/**
 * Flatten BaaS BalanceList (`{ items: [{ currency_code, available, blocked, future, … }] }`)
 * or legacy api-carteira flat `{ balance, blocked_balance, scheduled_balance }` into cents.
 */
const unwrapBalancePayload = (data) => {
  if (data == null || typeof data !== "object") return data;
  if (Array.isArray(data?.items) || Array.isArray(data)) return data;
  if (Array.isArray(data?.Items)) return { ...data, items: data.Items };
  if (data.data && (Array.isArray(data.data.items) || Array.isArray(data.data))) return data.data;
  return data;
};

const rowCurrencyCode = (row) =>
  String(
    row?.currency_code
      || row?.currencyCode
      || row?.currency_Code
      || row?.CurrencyCode
      || (typeof row?.currency === "string" ? row.currency : row?.currency?.code)
      || "",
  ).toUpperCase();

const isBrlBalanceRow = (row) => {
  const code = rowCurrencyCode(row);
  return code === "BRL" || code === "";
};

const sumBalanceField = (rows, ...keys) =>
  rows.reduce((sum, row) => {
    for (const key of keys) {
      const value = row?.[key];
      if (value != null && value !== "") {
        const n = Number(value);
        if (Number.isFinite(n)) return sum + n;
      }
    }
    return sum;
  }, 0);

export const pickPrimaryBalanceCents = (data) => {
  const payload = unwrapBalancePayload(data);
  const items = Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
      ? payload
      : null;

  if (items) {
    const brlItems = items.filter(isBrlBalanceRow);
    const pool = brlItems.length ? brlItems : items;
    const row = pool[0] || {};
    const allowOverdraft = pool.some(pickAllowOverdraft) || pickAllowOverdraft(row);
    const limitCents = Math.max(0, ...pool.map(pickLimitCents));
    const available = sumBalanceField(pool, "available", "Available");
    const blocked = sumBalanceField(pool, "blocked", "blocked_balance", "Blocked");
    const future = sumBalanceField(pool, "future", "scheduled_balance", "Future");
    const spendableRaw = sumBalanceField(pool, "spendable", "Spendable");
    const spendable =
      spendableRaw !== 0 || pool.some((item) => item?.spendable != null || item?.Spendable != null)
        ? spendableRaw
        : available + (allowOverdraft ? limitCents : 0);
    return {
      balance: available,
      blocked_balance: blocked,
      scheduled_balance: future,
      limit_cents: limitCents,
      allow_overdraft: allowOverdraft,
      spendable: Number.isFinite(spendable) ? spendable : available,
    };
  }

  const allowOverdraft = pickAllowOverdraft(payload);
  const limitCents = pickLimitCents(payload);
  const available = Number(payload?.balance ?? payload?.available ?? 0);
  const spendableRaw = payload?.spendable ?? payload?.Spendable;
  const spendable =
    spendableRaw != null && spendableRaw !== ""
      ? Number(spendableRaw)
      : available + (allowOverdraft ? limitCents : 0);

  return {
    balance: available,
    blocked_balance: Number(payload?.blocked_balance ?? payload?.blocked ?? 0),
    scheduled_balance: Number(payload?.scheduled_balance ?? payload?.future ?? 0),
    limit_cents: limitCents,
    allow_overdraft: allowOverdraft,
    spendable: Number.isFinite(spendable) ? spendable : available,
  };
};

export const isOverBalanceVisible = (flat) => {
  if (flat == null) return false;
  if (pickAllowOverdraft(flat) || flat.allow_overdraft === true) return true;
  if (Number(flat.limit_cents) > 0 || Number(flat.limitCents) > 0) return true;
  const available = Number.isFinite(flat.balance) ? flat.balance : Number(flat.availableCents) || 0;
  const spendable = Number.isFinite(flat.spendable)
    ? flat.spendable
    : Number(flat.spendableCents);
  return Number.isFinite(spendable) && spendable > available;
};

/**
 * Unused Over Balance (liberado).
 * Prefer spendable − cash when spendable includes the limit; if the API omitted
 * headroom but sent a ceiling (and cash is not negative), treat the full limit
 * as liberado. Drawing Over Balance always drives Available negative.
 */
export const remainingOverdraftCents = (flat) => {
  if (!isOverBalanceVisible(flat)) return 0;
  const limitCents = Math.max(
    0,
    Number(flat?.limit_cents) || Number(flat?.limitCents) || 0,
  );
  const available = Number.isFinite(flat?.balance) ? flat.balance : 0;
  const spendable = Number.isFinite(flat?.spendable) ? flat.spendable : available;
  const fromSpendable = Math.max(0, spendable - Math.max(available, 0));
  if (fromSpendable > 0) {
    return limitCents > 0 ? Math.min(limitCents, fromSpendable) : fromSpendable;
  }
  // Drawn into overdraft (cash negative).
  if (available < 0) {
    return limitCents > 0 ? Math.max(0, limitCents + available) : 0;
  }
  // Cash >= 0 with no spendable headroom → full ceiling still liberado
  // (API often sends spendable === available even when Limit is configured).
  return limitCents;
};

/**
 * Transfer/Pix view: gate on spendable; display spendable when Over Balance is on.
 * `balance` stays Available (unallocated).
 */
export const toTransferBalanceView = (data) => {
  const flat = pickPrimaryBalanceCents(data);
  const showLimit = isOverBalanceVisible(flat);
  const availableCents = Number.isFinite(flat.balance) ? flat.balance : 0;
  const spendableCents = Number.isFinite(flat.spendable) ? flat.spendable : availableCents;
  const limitCents = Number(flat.limit_cents) || 0;
  const remainingLimitCents = remainingOverdraftCents(flat);
  return {
    availableCents,
    spendableCents,
    limitCents,
    remainingLimitCents,
    allowOverdraft: pickAllowOverdraft(flat) || flat.allow_overdraft === true,
    showLimit,
    formattedAvailable: formatCurrency(availableCents),
    formattedSpendable: formatCurrency(spendableCents),
    formattedLimit: formatCurrency(limitCents),
    formattedRemainingLimit: formatCurrency(remainingLimitCents),
    formattedDisplay: formatCurrency(showLimit ? spendableCents : availableCents),
  };
};

export const normalizeAccountBalanceData = ({ data }) => {
  const flat = pickPrimaryBalanceCents(data);
  const availableCents = Number.isFinite(flat.balance) ? flat.balance : 0;
  const blockedCents = Number.isFinite(flat.blocked_balance) ? flat.blocked_balance : 0;
  const futureCents = Number.isFinite(flat.scheduled_balance) ? flat.scheduled_balance : 0;
  const spendableCents = Number.isFinite(flat.spendable) ? flat.spendable : availableCents;
  // Infer ceiling from spendable headroom when limit_cents was omitted/zeroed.
  const headroom = Math.max(0, spendableCents - availableCents);
  let limitCents = Math.max(0, Number(flat.limit_cents) || 0);
  if (limitCents <= 0 && headroom > 0) limitCents = headroom;
  const flatForLimit = { ...flat, limit_cents: limitCents, spendable: spendableCents };
  const showLimit = isOverBalanceVisible(flatForLimit);
  const remainingLimitCents = remainingOverdraftCents(flatForLimit);
  // Ledger cash: available + blocked + future. Over Balance is a card metric; PIX uses the bar.
  const consolidatedCents = availableCents + blockedCents + futureCents;
  return {
    balance: formatCurrency(availableCents),
    balanceColor: formatCurrencyColor(availableCents),
    blockedBalance: formatCurrency(blockedCents),
    blockedBalanceColor: formatCurrencyColor(blockedCents),
    futureBalance: formatCurrency(futureCents),
    futureBalanceColor: formatCurrencyColor(futureCents),
    limitBalance: formatCurrency(limitCents),
    limitBalanceColor: formatCurrencyColor(limitCents),
    remainingLimitBalance: formatCurrency(remainingLimitCents),
    remainingLimitBalanceColor: formatCurrencyColor(remainingLimitCents),
    spendableBalance: formatCurrency(spendableCents),
    spendableBalanceColor: formatCurrencyColor(spendableCents),
    consolidatedBalance: formatCurrency(consolidatedCents),
    consolidatedBalanceColor: formatCurrencyColor(consolidatedCents),
    allowOverdraft: pickAllowOverdraft(flat) || flat.allow_overdraft === true,
    showLimit,
    limitCents,
    remainingLimitCents,
    availableCents,
    blockedCents,
    futureCents,
    consolidatedCents,
    spendableCents,
  };
};

/**
 * Union two Saldos views of the same account without double-counting.
 * GET balances may return only the hold row (available 0, blocked 10) while
 * /me already has the cash row (available 111) — take the max of each pot.
 */
export const mergeNormalizedAccountBalances = (...views) => {
  const list = views.filter(
    (view) =>
      view
      && (Number.isFinite(view.availableCents)
        || Number.isFinite(view.blockedCents)
        || Number.isFinite(view.futureCents)
        || Number.isFinite(view.spendableCents)
        || Number.isFinite(view.limitCents)
        || view.showLimit === true
        || view.allowOverdraft === true),
  );
  if (!list.length) return null;
  // Keep signed Available (Over Balance drawdown goes negative). Do not floor at 0.
  const available = list.reduce((best, view) => {
    const value = Number(view.availableCents);
    if (!Number.isFinite(value)) return best;
    if (!Number.isFinite(best)) return value;
    // Prefer the most informative cash pot: highest when both non-negative,
    // otherwise the most negative drawdown when any row is below zero.
    if (value < 0 || best < 0) return Math.min(best, value);
    return Math.max(best, value);
  }, NaN);
  const availableCents = Number.isFinite(available) ? available : 0;
  const blocked = Math.max(0, ...list.map((view) => Number(view.blockedCents) || 0));
  const future = Math.max(0, ...list.map((view) => Number(view.futureCents) || 0));
  const limitCents = Math.max(0, ...list.map((view) => Number(view.limitCents) || 0));
  const allowOverdraft = list.some((view) => view.allowOverdraft === true);
  const maxRemaining = Math.max(
    0,
    ...list.map((view) => Number(view.remainingLimitCents) || 0),
  );
  const spendable = Math.max(
    availableCents,
    ...list.map((view) => Number(view.spendableCents) || 0),
    availableCents + maxRemaining,
    allowOverdraft && limitCents > 0 && maxRemaining <= 0
      ? availableCents + limitCents
      : 0,
  );
  return normalizeAccountBalanceData({
    data: {
      items: [
        {
          currency_code: "BRL",
          available: availableCents,
          blocked,
          future,
          spendable,
          limit_cents: limitCents,
          // Also send major so pickLimitCents can recover if cents were zeroed.
          limit: limitCents > 0 ? limitCents / 100 : 0,
          allow_overdraft: allowOverdraft,
        },
      ],
    },
  });
};

export const balancesFromMeAccount = (account) => {
  const items = Array.isArray(account?.balances) ? account.balances : [];
  return items.length ? normalizeAccountBalanceData({ data: { items } }) : null;
};

const accountLookupTokens = (row) => {
  const tokens = [];
  const push = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return;
    tokens.push(raw);
    const bare = raw.replace(/^acct_/i, "");
    if (bare && bare !== raw) tokens.push(bare);
    // 10023202001-0 ↔ 10023202001
    const withoutDigit = raw.replace(/-\d+$/, "");
    if (withoutDigit && withoutDigit !== raw) tokens.push(withoutDigit);
  };
  push(row?.id);
  push(row?.account_id);
  push(row?.accountId);
  push(row?.account_number);
  push(row?.accountNumber);
  const code = row?.account_code ?? row?.accountCode;
  const digit = row?.account_digit ?? row?.accountDigit;
  if (code != null && String(code).trim() !== "") {
    push(code);
    if (digit != null && String(digit).trim() !== "") {
      push(`${String(code).trim()}-${String(digit).trim()}`);
    }
  }
  return tokens;
};

export const findMeAccountById = (accounts, selectedId) => {
  const wanted = String(selectedId || "").trim().toLowerCase();
  if (!wanted || !Array.isArray(accounts)) return null;
  const wantedBare = wanted.replace(/^acct_/i, "");
  const wantedWithoutDigit = wanted.replace(/-\d+$/, "");
  return accounts.find((row) => {
    const ids = accountLookupTokens(row);
    return ids.includes(wanted)
      || (Boolean(wantedBare) && ids.includes(wantedBare))
      || (Boolean(wantedWithoutDigit)
        && wantedWithoutDigit !== wanted
        && ids.includes(wantedWithoutDigit));
  }) || null;
};

/** `/me` row for home Saldos: match id/number, else active/first when the stored id is legacy. */
export const resolveMeAccount = (accounts, selectedId) => {
  if (!Array.isArray(accounts) || accounts.length === 0) return null;
  const matched = findMeAccountById(accounts, selectedId);
  if (matched) return matched;
  const wanted = String(selectedId || "").trim().toLowerCase();
  if (!wanted || wanted === "investimento" || wanted === "individualizada") {
    return accounts.find((row) => row?.set_app_active) || accounts[0] || null;
  }
  return null;
};

export const normalizeBatchTransfers = ({ data }) =>
  (data || []).map((item) => ({
    document: item.document,
    amount: formatCurrency(item.amount),
    description: item.description,
    favoredName: item.favoredName,
    bank: `${item.bank.code} - ${item.bank.name}`,
    agencyBank: item.agencyBank,
    accountBank: item.accountBank,
    accountType: item.accountType,
    errors: item.errors,
  }));

/** Ensure BaaS / legacy statement rows have `created_at` string for day grouping. */
const normalizeStatementItemForUi = (item) => {
  const createdAtRaw = item?.created_at || item?.createdAt || "";
  const createdAt =
    typeof createdAtRaw === "string"
      ? createdAtRaw.includes("T")
        ? createdAtRaw
        : createdAtRaw
          ? `${createdAtRaw}T00:00:00`
          : new Date().toISOString()
      : createdAtRaw
        ? new Date(createdAtRaw).toISOString()
        : new Date().toISOString();

  const isRefund = isStatementRefund(item);
  const endToEndId = getStatementEndToEndId(item);
  const operationId =
    getStatementOperationId(item) || endToEndId || "";
  const base = {
    ...item,
    id: String(item?.id ?? item?.operation_id ?? `${createdAt}-${item?.amount ?? 0}`),
    created_at: createdAt,
    description: item?.description || item?.operation || "Movimentação",
    // Never fall back name → category (would duplicate categoria in the row).
    name: item?.name || "",
    amount: Number(item?.amount ?? 0),
    operation_amount: Number(item?.operation_amount ?? item?.amount ?? 0),
    balance_before: Number(item?.balance_before ?? 0),
    balance_after: Number(item?.balance_after ?? 0),
    operation_id: operationId,
    end_to_end_id: endToEndId,
    category: item?.category || null,
    is_refund: isRefund,
  };

  return {
    ...base,
    receipt: buildReceiptFromStatementItem(base),
  };
};

export const normalizeTransactionsData = ({ data }) => {
  const rawItems = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : null;

  if (!rawItems) return [];

  const items = rawItems.map(normalizeStatementItemForUi);
  return getTransactionsByDay(items) || [];
};

export const normalizeReceiptsData = ({ data }) => {
  const rawItems = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : null;

  if (!rawItems) return [];

  const items = rawItems.map(normalizeStatementItemForUi);
  return getTransactionsByDay(items) || [];
};

export const normalizeTransfersData = ({ data }) =>
  data?.itens
    ? data?.itens?.map((item) => ({
        ...item,
        value: formatCurrency(item?.value),
        valueColor: formatCurrencyColor(item?.value),
      }))
    : [];

export const normalizePaymentMethodsData = ({ data }) => data?.itens || [];

export const normalizeCardData = ({ data }) =>
  data
    ? data?.map((item) => ({
        ...item,
        value: formatCurrency(Math.abs(item?.amount)),
        valueColor: formatCurrencyColor(Math.abs(item?.amount)),
        icon: item?.category?.icon,
        title: item?.description,
        date: moment(item?.created_at).format("DD/MM/YYYY [às] HH:mm"),
        type: item?.operation || "",
      }))
    : [];

export const normalizeCardSettings = ({ data }) => ({
  ...data,
});

export const normalizeContactsData = ({ data }) => data?.itens || [];

export const normalizeCEPData = (values = {}) => ({
  cep: values.cep || "",
  logradouro: values.logradouro || values.street || "",
  bairro: values.bairro || values.neighborhood || "",
  localidade: values.localidade || values.city || "",
  uf: values.uf || values.state || "",
  complemento: values.complemento || values.complement || "",
});
